package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_error
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_shahnameh_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_shahnameh_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_shahnameh_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_tale_image_a11y
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/** Stateful entry for the Shahnameh tales browser (`/gems/shahnameh`). */
@Composable
fun ShahnamehRoute(
    onBack: () -> Unit,
    onOpenTale: (taleId: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ShahnamehViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    ShahnamehScreen(state = state, onBack = onBack, onOpenTale = onOpenTale, modifier = modifier)
}

/**
 * Shahnameh tales browser — a photo-forward list of prose retellings from Ferdowsi's epic. Tapping a
 * card opens the full story ([TaleDetailScreen]). Tokens + string resources only; start/end for RTL.
 */
@Composable
fun ShahnamehScreen(
    state: ShahnamehUiState,
    onBack: () -> Unit,
    onOpenTale: (taleId: String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize().background(AppTheme.colors.background).statusBarsPadding().navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.gems_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.gems_shahnameh_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
        }
        when {
            state.loading -> LoadingState()
            state.error -> EmptyState(title = stringResource(Res.string.gems_error))
            state.tales.isEmpty() -> EmptyState(
                title = stringResource(Res.string.gems_shahnameh_empty_title),
                description = stringResource(Res.string.gems_shahnameh_empty_desc),
            )
            else -> LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .widthIn(max = AppTheme.sizes.contentMaxWidthCompact),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                items(state.tales, key = { it.id }) { tale ->
                    TaleCard(tale = tale, onClick = { onOpenTale(tale.id) })
                }
            }
        }
    }
}

@Composable
private fun TaleCard(tale: Tale, onClick: () -> Unit) {
    val lang = AppTheme.language
    AppCard(modifier = Modifier.fillMaxWidth(), onClick = onClick) {
        Column {
            Box(modifier = Modifier.fillMaxWidth().height(AppTheme.sizes.resultImageHeight)) {
                BusinessCover(
                    imageUrl = tale.imageUrl,
                    iconName = "menu_book",
                    contentDescription = stringResource(Res.string.gems_tale_image_a11y),
                )
            }
            Column(
                modifier = Modifier.padding(AppTheme.spacing.md),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            ) {
                Text(
                    text = tale.title.resolve(lang),
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = tale.summary.resolve(lang),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}
